import { ColumnDetails, ColumnValue } from '../../../grid/types/common';
import { CategoriesConfig } from '../../../grid/genericGridSmartViews/types';
import { EASMAssetsInventoryNavigationParams } from '@nirvana/shared/navigation';
import { EasmFinding } from '../../types';
import { ListItem } from '../../../ui/gzSingleSelectList/types';

export interface Permissions {
  canHaveChildCompanies: boolean;
}

export interface Dependencies {
  api: {
    type: string;
  };
  translateFn: Function;
  getTimezone: Function;
  getUserLanguage: Function;
  permissions: Permissions;
  getUser: Function;
  getActiveCompany: Function;
  navigationParams: EASMAssetsInventoryNavigationParams | null;
  openEndpointDetailsWindow(endpointId: string): void;
}

export const SMART_VIEW_MAX_INPUT_LENGTH = 64;

export enum Column {
  NAME,
  TYPE,
  INVESTIGATION_STATUS,
  ASSIGNEE,
  PRIORITY,
  COUNTRY,
  OPERATOR,
  CERTIFICATE_REPORTS_ID,
  CERTIFICATE_REPORTS_ISSUER_NAME,
  CERTIFICATE_REPORTS_EXPIRATION_STATUS,
  CERTIFICATE_REPORTS_VALID_FROM,
  CERTIFICATE_REPORTS_VALID_UNTIL,
  CERTIFICATE_REPORTS_SERIAL_NUMBER,
  DNS_RECORDS_TYPE,
  DNS_RECORDS_MS,
  DNS_RECORDS_NS,
  DNS_RECORDS_CNAME,
  SERVICES_PORT,
  SERVICES_PROTOCOL,
  SERVICES_CVES,
  COMPANY,
  VULNERABLE_TO_ZONE_TRANSFER,
  ASSETS_RELATED_TO_ARTIFACT,
  DNS_RESOLUTION_STATUS,
  NOTES,
  NEW_ARTIFACTS,
  CHANGE_PROPERTY_ACTIONS,
}

export enum ColumnKeys {
  NAME = 'name',
  COMPANY = 'company',
  TYPE = 'type',
  INVESTIGATION_STATUS = 'investigationStatus',
  ASSIGNEE = 'assignee',
  PRIORITY = 'priority',
  DOMAIN = 'domain',
  IP = 'ip',
  COUNTRY = 'country',
  OPERATOR = 'operator',
  CERTIFICATE_REPORTS_ID = 'certificateId',
  CERTIFICATE_REPORTS_ISSUER_NAME = 'issuerName',
  CERTIFICATE_REPORTS_EXPIRATION_STATUS = 'expirationStatus',
  CERTIFICATE_REPORTS_VALID_FROM = 'notBefore',
  CERTIFICATE_REPORTS_VALID_UNTIL = 'notAfter',
  CERTIFICATE_REPORTS_SERIAL_NUMBER = 'serialNumber',
  DNS_RECORDS_TYPE = 'recordType',
  DNS_RECORDS_MS = 'mailServer',
  DNS_RECORDS_NS = 'nameServer',
  DNS_RECORDS_CNAME = 'cname',
  SERVICES_PORT = 'port',
  SERVICES_PROTOCOL = 'protocol',
  SERVICES_CVES = 'cves',
  VULNERABLE_TO_ZONE_TRANSFER = 'isVulnerable',
  ASSETS_RELATED_TO_ARTIFACT = 'assetsRelatedToArtifact',
  DNS_RESOLUTION_STATUS = 'status',
  NOTES = 'notes',
  NEW_ARTIFACTS = 'new',
  CHANGE_PROPERTY_ACTIONS = 'changePropertyActions',
}

export interface EasmArtifactsInventoryGridViewData {
  smartViewsCategories: CategoriesConfig;
  allColumns: Array<ColumnDetails<Column, ColumnKeys>>;
  predefinedFilterKeys: Array<ColumnKeys>;
  investigationStatusOptions: (ColumnValue | ListItem)[];
  assigneeOptions: ListItem[];
  priorityOptions: (ColumnValue | ListItem)[];
}

export const DEFAULT_PAGE_NUMBER = 1;
export const DEFAULT_PAGE_SIZE = 100;

export interface EasmArtifactsInventoryGridResponse {
  data: Array<EasmFinding>;
  total: number;
}

export interface EasmArtifactsInventoryGridGetRowsResponse {
  rawEvents: Array<EasmFinding>;
}

export enum DNSRecordType {
  SOA = 'SOA',
  MX = 'MX',
  NS = 'NS',
  CNAME = 'CNAME',
}

export enum EASMCountryType {
  ANDORRA = 'AD',
  UNITED_ARAB_EMIRATES = 'AE',
  AFGHANISTAN = 'AF',
  ANTIGUA_AND_BARBUDA = 'AG',
  ANGUILLA = 'AI',
  ALBANIA = 'AL',
  ARMENIA = 'AM',
  ANGOLA = 'AO',
  ANTARCTICA = 'AQ',
  ARGENTINA = 'AR',
  AMERICAN_SAMOA = 'AS',
  AUSTRIA = 'AT',
  AUSTRALIA = 'AU',
  ARUBA = 'AW',
  ALAND_ISLANDS = 'AX',
  AZERBAIJAN = 'AZ',
  BOSNIA_AND_HERZEGOVINA = 'BA',
  BARBADOS = 'BB',
  BANGLADESH = 'BD',
  BELGIUM = 'BE',
  BURKINA_FASO = 'BF',
  BULGARIA = 'BG',
  BAHRAIN = 'BH',
  BURUNDI = 'BI',
  BENIN = 'BJ',
  SAINT_BARTELEMEY = 'BL',
  BERMUDA = 'BM',
  BRUNEI_DARUSSALAM = 'BN',
  BOLIVIA = 'BO',
  BONAIRE_SAINT_EUSTATIUS_AND_SABA = 'BQ',
  BRAZIL = 'BR',
  BAHAMAS = 'BS',
  BHUTAN = 'BT',
  BOUVET_ISLAND = 'BV',
  BOTSWANA = 'BW',
  BELARUS = 'BY',
  BELIZE = 'BZ',
  CANADA = 'CA',
  COCOS_KEELING_ISLANDS = 'CC',
  CONGO_THE_DEMOCRATIC_REPUBLIC_OF_THE = 'CD',
  CENTRAL_AFRICAN_REPUBLIC = 'CF',
  CONGO = 'CG',
  SWITZERLAND = 'CH',
  COTE_D_IVOIRE = 'CI',
  COOK_ISLANDS = 'CK',
  CHILE = 'CL',
  CAMEROON = 'CM',
  CHINA = 'CN',
  COLOMBIA = 'CO',
  COSTA_RICA = 'CR',
  CAPE_VERDE = 'CV',
  CURACAO = 'CW',
  CHRISTMAS_ISLAND = 'CX',
  CYPRUS = 'CY',
  CZECH_REPUBLIC = 'CZ',
  GERMANY = 'DE',
  DJIBOUTI = 'DJ',
  DENMARK = 'DK',
  DOMINICA = 'DM',
  DOMINICAN_REPUBLIC = 'DO',
  ALGERIA = 'DZ',
  ECUADOR = 'EC',
  ESTONIA = 'EE',
  EGYPT = 'EG',
  WESTERN_SAHARA = 'EH',
  ERITREA = 'ER',
  SPAIN = 'ES',
  ETHIOPIA = 'ET',
  FINLAND = 'FI',
  FIJI = 'FJ',
  FALKLAND_ISLANDS_MALVINAS = 'FK',
  FEDERATED_STATES_OF_MICRONESIA = 'FM',
  FAROE_ISLANDS = 'FO',
  FRANCE = 'FR',
  GABON = 'GA',
  UNITED_KINGDOM = 'GB',
  GRENADA = 'GD',
  GEORGIA = 'GE',
  FRENCH_GUIANA = 'GF',
  GUERNSEY = 'GG',
  GHANA = 'GH',
  GIBRALTAR = 'GI',
  GREENLAND = 'GL',
  GAMBIA = 'GM',
  GUINEA = 'GN',
  GUADELOUPE = 'GP',
  EQUATORIAL_GUINEA = 'GQ',
  GREECE = 'GR',
  SOUTH_GEORGIA_AND_THE_SOUTH_SANDWICH_ISLANDS = 'GS',
  GUATEMALA = 'GT',
  GUAM = 'GU',
  GUINEA_BISSAU = 'GW',
  GUYANA = 'GY',
  HONG_KONG = 'HK',
  HEARD_ISLAND_AND_MCDONALD_ISLANDS = 'HM',
  HONDURAS = 'HN',
  CROATIA = 'HR',
  HAITI = 'HT',
  HUNGARY = 'HU',
  INDONESIA = 'ID',
  IRELAND = 'IE',
  ISRAEL = 'IL',
  ISLE_OF_MAN = 'IM',
  INDIA = 'IN',
  BRITISH_INDIAN_OCEAN_TERRITORY = 'IO',
  IRAQ = 'IQ',
  ICELAND = 'IS',
  ITALY = 'IT',
  JERSEY = 'JE',
  JAMAICA = 'JM',
  JORDAN = 'JO',
  JAPAN = 'JP',
  KENYA = 'KE',
  KYRGYZSTAN = 'KG',
  KOSOVO = 'XK',
  CAMBODIA = 'KH',
  KIRIBATI = 'KI',
  COMOROS = 'KM',
  SAINT_KITTS_AND_NEVIS = 'KN',
  REPUBLIC_OF_KOREA = 'KR',
  KUWAIT = 'KW',
  CAYMAN_ISLANDS = 'KY',
  KAZAKHSTAN = 'KZ',
  LAO_PEOPLE_S_DEMOCRATIC_REPUBLIC = 'LA',
  LEBANON = 'LB',
  SAINT_LUCIA = 'LC',
  LIECHTENSTEIN = 'LI',
  SRI_LANKA = 'LK',
  LIBERIA = 'LR',
  LESOTHO = 'LS',
  LITHUANIA = 'LT',
  LUXEMBOURG = 'LU',
  LATVIA = 'LV',
  LIBYAN_ARAB_JAMAHIRIYA = 'LY',
  MOROCCO = 'MA',
  MONACO = 'MC',
  REPUBLIC_OF_MOLDOVA = 'MD',
  MONTENEGRO = 'ME',
  SAINT_MARTIN = 'MF',
  MADAGASCAR = 'MG',
  MARSHALL_ISLANDS = 'MH',
  MACEDONIA = 'MK',
  MALI = 'ML',
  MYANMAR = 'MM',
  MONGOLIA = 'MN',
  MACAO = 'MO',
  NORTHERN_MARIANA_ISLANDS = 'MP',
  MARTINIQUE = 'MQ',
  MAURITANIA = 'MR',
  MONTSERRAT = 'MS',
  MALTA = 'MT',
  MAURITIUS = 'MU',
  MALDIVES = 'MV',
  MALAWI = 'MW',
  MEXICO = 'MX',
  MALAYSIA = 'MY',
  MOZAMBIQUE = 'MZ',
  NAMIBIA = 'NA',
  NEW_CALEDONIA = 'NC',
  NIGER = 'NE',
  NORFOLK_ISLAND = 'NF',
  NIGERIA = 'NG',
  NICARAGUA = 'NI',
  NETHERLANDS = 'NL',
  NORWAY = 'NO',
  NEPAL = 'NP',
  NAURU = 'NR',
  NIUE = 'NU',
  NEW_ZEALAND = 'NZ',
  OMAN = 'OM',
  PANAMA = 'PA',
  PERU = 'PE',
  FRENCH_POLYNESIA = 'PF',
  PAPUA_NEW_GUINEA = 'PG',
  PHILIPPINES = 'PH',
  PAKISTAN = 'PK',
  POLAND = 'PL',
  SAINT_PIERRE_AND_MIQUELON = 'PM',
  PITCAIRN = 'PN',
  PUERTO_RICO = 'PR',
  PALESTINIAN_TERRITORY = 'PS',
  PORTUGAL = 'PT',
  PALAU = 'PW',
  PARAGUAY = 'PY',
  QATAR = 'QA',
  REUNION = 'RE',
  ROMANIA = 'RO',
  SERBIA = 'RS',
  RUSSIAN_FEDERATION = 'RU',
  RWANDA = 'RW',
  SAUDI_ARABIA = 'SA',
  SOLOMON_ISLANDS = 'SB',
  SEYCHELLES = 'SC',
  SUDAN = 'SD',
  SWEDEN = 'SE',
  SINGAPORE = 'SG',
  SAINT_HELENA = 'SH',
  SLOVENIA = 'SI',
  SVALBARD_AND_JAN_MAYEN = 'SJ',
  SLOVAKIA = 'SK',
  SIERRA_LEONE = 'SL',
  SAN_MARINO = 'SM',
  SENEGAL = 'SN',
  SOMALIA = 'SO',
  SURINAME = 'SR',
  SOUTH_SUDAN = 'SS',
  SAO_TOME_AND_PRINCIPE = 'ST',
  EL_SALVADOR = 'SV',
  SINT_MAARTEN = 'SX',
  SWAZILAND = 'SZ',
  TURKS_AND_CAICOS_ISLANDS = 'TC',
  CHAD = 'TD',
  FRENCH_SOUTHERN_TERRITORIES = 'TF',
  TOGO = 'TG',
  THAILAND = 'TH',
  TAJIKISTAN = 'TJ',
  TOKELAU = 'TK',
  TIMOR_LESTE = 'TL',
  TURKMENISTAN = 'TM',
  TUNISIA = 'TN',
  TONGA = 'TO',
  TURKEY = 'TR',
  TRINIDAD_AND_TOBAGO = 'TT',
  TUVALU = 'TV',
  TAIWAN = 'TW',
  UNITED_REPUBLIC_OF_TANZANIA = 'TZ',
  UKRAINE = 'UA',
  UGANDA = 'UG',
  UNITED_STATES_MINOR_OUTLYING_ISLANDS = 'UM',
  UNITED_STATES = 'US',
  URUGUAY = 'UY',
  UZBEKISTAN = 'UZ',
  HOLY_SEE_VATICAN_CITY_STATE = 'VA',
  SAINT_VINCENT_AND_THE_GRENADINES = 'VC',
  VENEZUELA = 'VE',
  VIRGIN_ISLANDS_BRITISH = 'VG',
  VIRGIN_ISLANDS_U_S = 'VI',
  VIETNAM = 'VN',
  VANUATU = 'VU',
  WALLIS_AND_FUTUNA = 'WF',
  SAMOA = 'WS',
  YEMEN = 'YE',
  MAYOTTE = 'YT',
  SOUTH_AFRICA = 'ZA',
  ZAMBIA = 'ZM',
  ZIMBABWE = 'ZW',
}
